import type { ApiAccountFirmographics } from './accounts.js';
import type { RecordScores } from './scores.js';

export type AccountKind =
  | 'owner'
  | 'developer'
  | 'architect'
  | 'landscape_architect'
  | 'engineer'
  | 'consultant'
  | 'law_firm'
  | 'contractor'
  | 'agency'
  | 'unknown';

// Matches ApiAccountType from lib/accounts.ts (duplicated here to avoid a
// circular import between mock-accounts → accounts → mock-accounts).
export type AccountApiType =
  | 'developer'
  | 'architect'
  | 'gc'
  | 'engineer'
  | 'agency'
  | 'law_firm'
  | 'consultant'
  | 'landscape_architect'
  | 'other';

/** Maps an API account type to the display kind used by KIND_META. */
export const ACCOUNT_TYPE_TO_KIND: Record<AccountApiType, AccountKind> = {
  developer: 'developer',
  architect: 'architect',
  engineer: 'engineer',
  gc: 'contractor',
  agency: 'agency',
  law_firm: 'law_firm',
  consultant: 'consultant',
  landscape_architect: 'landscape_architect',
  other: 'unknown',
};

export const KIND_META: Record<
  AccountKind,
  { label: string; variant: 'amber' | 'purple' | 'blue' | 'green' | 'gray' }
> = {
  owner: { label: 'Owner', variant: 'gray' },
  developer: { label: 'Developer', variant: 'blue' },
  architect: { label: 'Architect', variant: 'purple' },
  landscape_architect: { label: 'Landscape architect', variant: 'green' },
  engineer: { label: 'Engineer', variant: 'amber' },
  consultant: { label: 'Consultant', variant: 'gray' },
  law_firm: { label: 'Law firm', variant: 'gray' },
  contractor: { label: 'Contractor', variant: 'green' },
  agency: { label: 'Agency', variant: 'gray' },
  unknown: { label: 'Unknown', variant: 'gray' },
};

export interface AccountRow {
  id: string;
  name: string;
  kind: AccountKind;
  /** Full set of account types from the API; empty = unknown. */
  account_types: AccountApiType[];
  website: string | null;
  /** Resolved company domains; first entry is the display/link-out fallback when website is null. */
  domains?: string[];
  /** The firm's LinkedIn page, when Parcel holds one; an older API omits it. */
  linkedinUrl?: string | null;
  /** Size, industry, age and revenue from Parcel's people search; an older API omits it. */
  firmographics?: ApiAccountFirmographics | null;
  city: string;
  state: string;
  projectsCount: number;
  contactsCount: number;
  /** This workspace's ICP Score: `{icp: {score, bucket, matched}}`, `{icp: null}` when unscored. */
  scores?: RecordScores;
  lastActivityAt: string | null;
  createdAt: string;
}

export const MOCK_ACCOUNTS: AccountRow[] = [
  {
    id: 'a_001',
    name: 'Park Tower Group',
    kind: 'developer',
    account_types: ['developer'],
    website: 'parktowergroup.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 12,
    contactsCount: 5,
    scores: { icp: { score: 86, bucket: 'tier_1', matched: [] } },
    lastActivityAt: '2026-05-12T09:14:00Z',
    createdAt: '2025-08-04T13:11:00Z',
  },
  {
    id: 'a_002',
    name: 'Two Trees Management',
    kind: 'developer',
    account_types: ['developer'],
    website: 'twotreesny.com',
    city: 'Brooklyn',
    state: 'NY',
    projectsCount: 8,
    contactsCount: 4,
    scores: { icp: { score: 74, bucket: 'tier_2', matched: [] } },
    lastActivityAt: '2026-05-10T16:02:00Z',
    createdAt: '2025-09-12T10:00:00Z',
  },
  {
    id: 'a_003',
    name: 'FXCollaborative',
    kind: 'architect',
    account_types: ['architect'],
    website: 'fxcollaborative.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 22,
    contactsCount: 7,
    scores: { icp: { score: 64, bucket: 'tier_2', matched: [] } },
    lastActivityAt: '2026-05-12T09:14:00Z',
    createdAt: '2025-07-18T09:30:00Z',
  },
  {
    id: 'a_004',
    name: 'Innovo Property Group',
    kind: 'developer',
    account_types: ['developer'],
    website: 'innovopg.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 4,
    contactsCount: 2,
    scores: { icp: null },
    lastActivityAt: '2026-05-09T11:35:00Z',
    createdAt: '2026-01-22T15:45:00Z',
  },
  {
    id: 'a_005',
    name: 'Steiner NYC',
    kind: 'developer',
    account_types: ['developer'],
    website: 'steinernyc.com',
    city: 'Brooklyn',
    state: 'NY',
    projectsCount: 3,
    contactsCount: 1,
    scores: { icp: { score: 41, bucket: 'tier_3', matched: [] } },
    lastActivityAt: '2026-05-08T13:50:00Z',
    createdAt: '2026-02-05T11:20:00Z',
  },
  {
    id: 'a_006',
    name: 'Marvel Architects',
    kind: 'architect',
    account_types: ['architect'],
    website: 'marvelarchitects.com',
    city: 'Brooklyn',
    state: 'NY',
    projectsCount: 15,
    contactsCount: 6,
    scores: { icp: { score: 58, bucket: 'tier_2', matched: [] } },
    lastActivityAt: '2026-05-08T13:50:00Z',
    createdAt: '2025-10-14T08:00:00Z',
  },
  {
    id: 'a_007',
    name: 'Alloy Development',
    kind: 'developer',
    account_types: ['developer'],
    website: 'alloyllc.com',
    city: 'Brooklyn',
    state: 'NY',
    projectsCount: 9,
    contactsCount: 3,
    scores: { icp: { score: 0, bucket: 'disqualified', matched: [] } },
    lastActivityAt: '2026-05-07T08:21:00Z',
    createdAt: '2025-06-30T12:00:00Z',
  },
  {
    id: 'a_008',
    name: 'Industry City Partners',
    kind: 'owner',
    account_types: [],
    website: 'industrycity.com',
    city: 'Brooklyn',
    state: 'NY',
    projectsCount: 6,
    contactsCount: 4,
    scores: { icp: null },
    lastActivityAt: '2026-05-06T15:11:00Z',
    createdAt: '2025-11-09T17:30:00Z',
  },
  {
    id: 'a_009',
    name: 'Prologis',
    kind: 'developer',
    account_types: ['developer'],
    website: 'prologis.com',
    city: 'San Francisco',
    state: 'CA',
    projectsCount: 2,
    contactsCount: 1,
    scores: { icp: { score: 49, bucket: 'tier_3', matched: [] } },
    lastActivityAt: '2026-05-05T10:00:00Z',
    createdAt: '2026-03-01T09:00:00Z',
  },
  {
    id: 'a_010',
    name: 'Ware Malcomb',
    kind: 'architect',
    account_types: ['architect'],
    website: 'waremalcomb.com',
    city: 'Irvine',
    state: 'CA',
    projectsCount: 5,
    contactsCount: 2,
    scores: { icp: { score: 33, bucket: 'tier_3', matched: [] } },
    lastActivityAt: '2026-05-05T10:00:00Z',
    createdAt: '2026-02-18T14:00:00Z',
  },
  {
    id: 'a_011',
    name: 'Hidrock Properties',
    kind: 'developer',
    account_types: ['developer'],
    website: 'hidrock.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 7,
    contactsCount: 3,
    scores: { icp: { score: 69, bucket: 'tier_2', matched: [] } },
    lastActivityAt: '2026-05-04T17:42:00Z',
    createdAt: '2025-12-10T11:00:00Z',
  },
  {
    id: 'a_012',
    name: 'Gene Kaufman Architect',
    kind: 'architect',
    account_types: ['architect'],
    website: 'genekaufman.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 11,
    contactsCount: 4,
    scores: { icp: { score: 52, bucket: 'tier_2', matched: [] } },
    lastActivityAt: '2026-05-04T17:42:00Z',
    createdAt: '2025-08-22T10:30:00Z',
  },
  {
    id: 'a_013',
    name: 'ODA New York',
    kind: 'architect',
    account_types: ['architect'],
    website: 'oda-ny.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 14,
    contactsCount: 5,
    scores: { icp: { score: 78, bucket: 'tier_1', matched: [] } },
    lastActivityAt: '2026-05-10T16:02:00Z',
    createdAt: '2025-07-01T09:00:00Z',
  },
  {
    id: 'a_014',
    name: 'KSS Architects',
    kind: 'architect',
    account_types: ['architect'],
    website: 'kssarchitects.com',
    city: 'Princeton',
    state: 'NJ',
    projectsCount: 6,
    contactsCount: 2,
    scores: { icp: null },
    lastActivityAt: '2026-05-09T11:35:00Z',
    createdAt: '2025-11-15T14:00:00Z',
  },
  {
    id: 'a_015',
    name: 'Beyer Blinder Belle',
    kind: 'architect',
    account_types: ['architect'],
    website: 'beyerblinderbelle.com',
    city: 'New York',
    state: 'NY',
    projectsCount: 4,
    contactsCount: 2,
    scores: { icp: { score: 47, bucket: 'tier_3', matched: [] } },
    lastActivityAt: '2026-05-06T15:11:00Z',
    createdAt: '2026-01-08T16:00:00Z',
  },
];

export function formatRelativeDate(iso: string | null | undefined) {
  if (!iso) return '—';

  const d = new Date(iso);
  const now = Date.now();
  const diffDays = Math.floor((now - d.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
